import React, { useCallback, useEffect, useState } from 'react';
import ViolationHeader from '../ViolationHeader/ViolationHeader';
import ViolationScheduleInfo from '../ViolationScheduleInfo/ViolationScheduleInfo';
import Notes from '../../Notes/Notes';

import { ViolationDetailsContainer, ViolationsInfoContainer } from './ViolationDetails.styles';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectSelectedViolation } from '../../../redux/violationsReport/violationsReportSlice';
import { fetchBreak } from '../../../redux/breaks/breaksThunks';
import { fetchShift } from '../../../redux/shifts/shiftsThunks';
import { updateViolation } from '../../../redux/violationsReport/violationsThunks';
import { VIOLATION_TYPES } from '../../../utils/constants/reports/violationReports.constants';
import { selectSelectedShift } from '../../../redux/shifts/shiftsSlice';
import { selectSelectedBreak } from '../../../redux/breaks/breaksSlice';

// Constants
const DEBOUNCE_DELAY_MS = 100;

const ViolationDetails = ({ isBreakViolation }) => {
  const dispatch = useDispatch();

  // SELECTORS
  const violation = useSelector(selectSelectedViolation, shallowEqual); // use shallowEqual to avoid re-rendering
  const selectedShiftDetails = useSelector(selectSelectedShift); // Get all the details of the selected shift
  const selectedBreakDetails = useSelector(selectSelectedBreak); // Gets the details of the selected break

  // STATES
  const [updatedNotes, setUpdatedNotes] = useState(''); // is IMPORTANT to use this variable instead of the value from redux
  const [isLoadingShift, setIsLoadingShift] = useState(false);
  const [isLoadingBreak, setIsLoadingBreak] = useState(false);

  // VARIABLES
  const isLoading = isLoadingShift || isLoadingBreak;
  const isMissedViolation = violation.type.type === VIOLATION_TYPES.MISSED;

  // Save note the local state in the onChange event
  const handleSaveNote = useCallback((note) => {
    setUpdatedNotes(note);
  }, []);

  // After a note has been saved, when the input loses focus, to avoid re-rendering every time a change occurs
  const handleBlur = useCallback(() => {
    dispatch(updateViolation({ notes: updatedNotes }));
  }, [updatedNotes, dispatch]);

  // Avoid to re-render if the violation notes are not updated
  useEffect(() => {
    if (violation?.notes !== updatedNotes) {
      setUpdatedNotes(violation.notes || '');
    }
  }, [violation?.notes !== updatedNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup loading states only on component unmount
  useEffect(() => {
    return () => {
      setIsLoadingShift(false);
      setIsLoadingBreak(false);
    };
  }, []);

  //Fetch shift or break details when a violation is selected
  useEffect(() => {
    if (!violation?.id) return;

    // Reset loading states when switching violations
    setIsLoadingShift(false);
    setIsLoadingBreak(false);

    // Add a small delay to prevent rapid-fire API calls
    const timeoutId = setTimeout(() => {
      if (isBreakViolation) {
        const breakId = violation?.details?.break?.breakId;
        if (!breakId) {
          console.warn('No break ID found for break violation');
          return;
        }
        setIsLoadingBreak(true);
        dispatch(fetchBreak({ shiftId: violation.shiftId, breakId }))
          .unwrap()
          .then(() => setIsLoadingBreak(false))
          .catch(() => setIsLoadingBreak(false));
      } else {
        if (!violation.shiftId) {
          console.warn('No shift ID found for shift violation');
          return;
        }
        setIsLoadingShift(true);
        dispatch(fetchShift(violation.shiftId))
          .unwrap()
          .then(() => setIsLoadingShift(false))
          .catch(() => setIsLoadingShift(false));
      }
    }, DEBOUNCE_DELAY_MS); // 100ms delay to prevent race conditions

    // Cleanup timeout if violation changes
    return () => clearTimeout(timeoutId);
  }, [violation?.id, isBreakViolation, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ViolationDetailsContainer>
      <ViolationHeader violation={violation} />

      <ViolationsInfoContainer>
        <ViolationScheduleInfo
          status={violation?.status}
          isBreakViolation={isBreakViolation}
          isLoading={isLoading}
          shiftDetails={isBreakViolation ? null : selectedShiftDetails}
          breakDetails={isBreakViolation ? selectedBreakDetails : null}
          isMissedViolation={isMissedViolation}
        />
      </ViolationsInfoContainer>

      <Notes
        title="Violation Notes"
        handleBlur={handleBlur}
        placeholder="Add notes to this violation"
        note={updatedNotes}
        handleSaveNote={handleSaveNote}
      />
    </ViolationDetailsContainer>
  );
};

export default ViolationDetails;
